import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Building2,
  Check,
  Info,
  Loader2,
  Phone,
  PhoneForwarded,
  RefreshCw,
  Search,
  ShieldAlert,
  FileUp,
  Upload,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { SettingsPanel, SettingsFieldGrid } from '@/components/settings/SettingsConsole';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import voiceService, {
  type NumberRequirement,
  type PendingRequirementOrder,
  type AiMode,
  type AssistantInput,
  type AvailableNumber,
  type VoiceSettings,
} from '@/services/voice.service';
import meetingsService from '@/services/meetings.service';
import useVoiceStore from '@/stores/voiceStore';

const COUNTRIES = ['DE', 'AT', 'NL', 'FR', 'IT', 'ES', 'PL', 'PT', 'GB', 'US'];

/**
 * Einstellungen → Telefonie.
 *
 * Bewusst ohne Telnyx-Interna: API-Key und Connection werden serverseitig
 * gestellt bzw. beim ersten Kauf automatisch angelegt. Der Kunde sieht nur
 * seine Rufnummer.
 */
export default function VoiceSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Telefonie ist ORGANISATIONS-Sache: jede Organisation (jeder Shop) hat ihre
  // eigene Nummer, eigene KI, eigene Einstellungen. Wer in mehreren
  // Organisationen Mitglied ist, wählt hier oben, welche er verwaltet.
  const orgsQuery = useQuery({
    queryKey: ['voice-orgs'],
    queryFn: () => meetingsService.organizations(),
    staleTime: 5 * 60_000,
  });
  const orgs = orgsQuery.data || [];
  const [orgId, setOrgId] = useState('');
  useEffect(() => {
    if (!orgId && orgs.length) setOrgId(orgs[0].id);
  }, [orgs, orgId]);
  const oid = orgId || undefined;

  const { data, isLoading } = useQuery<VoiceSettings>({
    queryKey: ['voice-settings', orgId],
    queryFn: () => voiceService.getSettings(oid),
    enabled: Boolean(orgId) || (orgsQuery.isFetched && orgs.length === 0),
  });

  const numbersQuery = useQuery({
    queryKey: ['voice-numbers', orgId],
    queryFn: () => voiceService.listNumbers(oid),
    retry: false,
  });

  const [country, setCountry] = useState('DE');
  const [locality, setLocality] = useState('');
  const [results, setResults] = useState<AvailableNumber[] | null>(null);
  const [externalNumber, setExternalNumber] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [recordAll, setRecordAll] = useState(false);

  useEffect(() => {
    if (!data) return;
    setIsActive(Boolean(data.config.is_active));
    setRecordAll(Boolean(data.config.record_all));
    setExternalNumber(data.config.external_number || '');
  }, [data]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['voice-settings'] });
    queryClient.invalidateQueries({ queryKey: ['voice-numbers'] });
    queryClient.invalidateQueries({ queryKey: ['voice-status'] });
    queryClient.invalidateQueries({ queryKey: ['voice-lines'] });
    // Leitungen haben sich geändert — WebRTC-Registrierungen nachziehen.
    void useVoiceStore.getState().reconnect();
  };

  const searchMutation = useMutation({
    mutationFn: () => voiceService.searchNumbers({ country, locality: locality.trim() || undefined, organizationId: oid }),
    onSuccess: (r) => setResults(r),
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || t('settings:voice.searchFailed', 'Suche fehlgeschlagen')),
  });

  const orderMutation = useMutation({
    mutationFn: (number: string) => voiceService.orderNumber(number, oid),
    onSuccess: (r) => {
      setResults(null);
      invalidateAll();
      toast.success(
        r.status === 'success'
          ? t('settings:voice.orderDone', 'Rufnummer {{n}} ist aktiv', { n: r.phone_number })
          : t('settings:voice.orderPending', 'Bestellung läuft — Status gleich aktualisieren'),
      );
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || t('settings:voice.orderFailed', 'Bestellung fehlgeschlagen')),
  });

  const refreshMutation = useMutation({
    mutationFn: () => voiceService.refreshNumbers(oid),
    onSuccess: (r) => {
      invalidateAll();
      toast.success(
        r.active
          ? t('settings:voice.refreshActive', 'Rufnummer ist aktiv')
          : t('settings:voice.refreshPending', 'Bestellung läuft noch'),
      );
    },
  });

  const activationMutation = useMutation({
    mutationFn: (next: boolean) => voiceService.saveSettings({ is_active: next }, oid),
    onSuccess: (res) => {
      queryClient.setQueryData(['voice-settings', orgId], res);
      queryClient.invalidateQueries({ queryKey: ['voice-status'] });
    },
    onError: (e: any) => {
      setIsActive(Boolean(data?.config.is_active));
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen'));
    },
  });

  const recordAllMutation = useMutation({
    mutationFn: (next: boolean) => voiceService.saveSettings({ record_all: next }, oid),
    onSuccess: (res) => queryClient.setQueryData(['voice-settings', orgId], res),
    onError: (e: any) => {
      setRecordAll(Boolean(data?.config.record_all));
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen'));
    },
  });

  const externalMutation = useMutation({
    mutationFn: () => voiceService.setExternalNumber(externalNumber.trim() || null, oid),
    onSuccess: () => {
      invalidateAll();
      toast.success(t('settings:voice.externalSaved', 'Nummer gespeichert'));
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const ownNumbers = numbersQuery.data || [];
  const primary = data?.config.caller_id;
  const otherOrgs = orgs.filter((o) => o.id !== orgId);
  const hasActiveNumber = ownNumbers.some((n) => n.status === 'active');
  const callingEnabled = Boolean(data?.callingEnabled);
  const pending = Boolean(primary) && !hasActiveNumber;

  return (
    <div className="space-y-4">
      {/* --- Organisation wählen (nur bei Mitgliedschaft in mehreren) ------ */}
      {orgs.length > 1 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Building2 className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800">
              {t('settings:voice.org.title', 'Organisation')}
            </p>
            <p className="text-xs text-slate-500">
              {t('settings:voice.org.hint', 'Nummer, KI und Einstellungen gelten je Organisation.')}
            </p>
          </div>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="h-9 w-full sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* --- Aktuelle Rufnummer ------------------------------------------- */}
      <SettingsPanel
        title={t('settings:voice.number.title', 'Ihre Rufnummer')}
        description={t('settings:voice.number.description', 'Über diese Nummer telefonieren Ihre Agenten direkt im Browser.')}
        action={
          primary && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
            >
              {refreshMutation.isPending ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
              )}
              {t('settings:voice.refresh', 'Status aktualisieren')}
            </Button>
          )
        }
      >
        {primary ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <Phone className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-lg font-semibold tabular-nums text-slate-900">{primary}</p>
                {pending ? (
                  <Badge variant="outline" className="mt-1 border-amber-200 bg-amber-50 text-amber-700">
                    {t('settings:voice.statusPending', 'Bestellung läuft')}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="mt-1 border-emerald-200 bg-emerald-50 text-emerald-700">
                    <Check className="mr-1 h-3 w-3" />
                    {t('settings:voice.statusActive', 'Aktiv')}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5">
              <Switch
                checked={isActive}
                disabled={pending || activationMutation.isPending}
                onCheckedChange={(next) => { setIsActive(next); activationMutation.mutate(next); }}
              />
              <span className="text-sm text-slate-600">
                {isActive
                  ? t('settings:voice.activeOn', 'Agenten können telefonieren')
                  : t('settings:voice.activeOff', 'Telefonie ist deaktiviert')}
              </span>
            </div>

            {/* Auto-Aufzeichnung — bewusst mit Rechtshinweis direkt daneben:
                ohne Ansage/Einwilligung ist Mitschneiden in DE strafbar (§201 StGB). */}
            <div className="rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5">
              <div className="flex items-center gap-3">
                <Switch
                  checked={recordAll}
                  disabled={recordAllMutation.isPending}
                  onCheckedChange={(next) => { setRecordAll(next); recordAllMutation.mutate(next); }}
                />
                <span className="text-sm text-slate-600">
                  {t('settings:voice.recordAll', 'Alle eingehenden Anrufe automatisch aufzeichnen')}
                </span>
              </div>
              <p className="mt-1.5 pl-12 text-xs text-amber-700">
                {t(
                  'settings:voice.recordAllHint',
                  'Achtung: Aufzeichnung ohne Einwilligung der Anrufer ist in Deutschland strafbar (§201 StGB). Nur mit vorgeschalteter Ansage verwenden — die Verantwortung liegt beim Betreiber.',
                )}
              </p>
            </div>

            {/* Nummer in eine andere eigene Organisation verschieben — für
                Nummern, die vor der Org-Umstellung „irgendwo" gekauft wurden. */}
            {otherOrgs.length > 0 && (
              <AssignNumberRow number={primary!} targets={otherOrgs} onDone={invalidateAll} />
            )}
          </div>
        ) : (
          <p className="flex items-start gap-2 text-sm text-slate-500">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            {t('settings:voice.noNumber', 'Noch keine Rufnummer. Wählen Sie unten eine aus — die Einrichtung passiert automatisch.')}
          </p>
        )}
      </SettingsPanel>

      {/* --- Neue Nummer ---------------------------------------------------- */}
      <SettingsPanel
        title={t('settings:voice.buy.title', 'Rufnummer auswählen')}
        description={t('settings:voice.buy.description', 'Land und optional eine Stadt wählen. Die Nummer wird sofort eingerichtet.')}
      >
        {/* Sabit ızgara: etiketler üstte, üç kontrol AYNI yükseklikte hizalı.
            İpucu satırın ALTINDA — sütunun içinde olsaydı items-end hizasını
            bozup Land/Suchen'i aşağı itiyordu (yaşandı). */}
        <div className="grid gap-3 sm:grid-cols-[110px_minmax(0,1fr)_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label>{t('settings:voice.country', 'Land')}</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="voice-locality">{t('settings:voice.city', 'Stadt (optional)')}</Label>
            <Input
              id="voice-locality"
              className="h-10"
              value={locality}
              onChange={(e) => setLocality(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') searchMutation.mutate(); }}
              placeholder="Köln"
            />
          </div>
          <Button className="h-10" onClick={() => searchMutation.mutate()} disabled={searchMutation.isPending}>
            {searchMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-2 h-4 w-4" />
            )}
            {t('settings:voice.search', 'Suchen')}
          </Button>
        </div>

        {/* Telnyx filtert exakt: "Koeln" liefert nichts, "Köln" schon. */}
        <p className="mt-2 text-xs text-slate-500">
          {t('settings:voice.cityHint', 'Genaue Schreibweise nötig, z. B. „Köln" statt „Koeln".')}
        </p>

        {results && results.length === 0 && (
          <p className="mt-4 text-sm text-slate-500">
            {t('settings:voice.noResults', 'Keine Nummern gefunden. Andere Stadt oder anderes Land versuchen.')}
          </p>
        )}

        {results && results.length > 0 && (
          <>
            {results.some((r) => r.masked) && (
              <p className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {t(
                  'settings:voice.maskedHint',
                  'Die Ziffern sind verdeckt, solange das Telefonie-Konto nicht verifiziert ist. Die Bestellung schlägt bis dahin fehl.',
                )}
              </p>
            )}
            <ul className="mt-4 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
              {results.map((n) => (
                <li key={n.phone_number} className="flex items-center gap-3 bg-white px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium tabular-nums text-slate-900">{n.phone_number}</p>
                    <p className="truncate text-xs text-slate-500">
                      {[n.region, n.number_type].filter(Boolean).join(' · ')}
                      {n.monthly_cost && ` · ${n.monthly_cost} ${n.currency || ''}/Monat`}
                      {n.requirements.length > 0 && ` · ${t('settings:voice.docsNeeded', 'Nachweis nötig')}`}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={orderMutation.isPending || n.masked}
                    onClick={() => orderMutation.mutate(n.phone_number)}
                  >
                    {orderMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      t('settings:voice.take', 'Übernehmen')
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
      </SettingsPanel>

      {/* --- Regulatorische Nachweise (z. B. DE-Nummern) --------------------- */}
      <RequirementsPanel orgId={oid} />

      {/* --- KI-Assistent ---------------------------------------------------- */}
      <AssistantPanel enabled={callingEnabled} orgId={oid} />

      {/* --- Vorhandene Nummer weiterleiten ---------------------------------- */}
      <SettingsPanel
        title={t('settings:voice.forward.title', 'Vorhandene Nummer weiterleiten')}
        description={t(
          'settings:voice.forward.description',
          'Ihre bisherige Nummer bleibt bei Ihrem Anbieter. Richten Sie dort eine Weiterleitung auf Ihre VynDesk-Nummer ein.',
        )}
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[200px] flex-1 space-y-1.5">
            <Label htmlFor="voice-external">{t('settings:voice.externalNumber', 'Ihre bisherige Nummer')}</Label>
            <Input
              id="voice-external"
              value={externalNumber}
              onChange={(e) => setExternalNumber(e.target.value)}
              placeholder="+4930123456"
            />
          </div>
          <Button variant="outline" onClick={() => externalMutation.mutate()} disabled={externalMutation.isPending}>
            {externalMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common:action.save', 'Speichern')}
          </Button>
        </div>

        {primary && (
          <p className="mt-3 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <PhoneForwarded className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            {t('settings:voice.forwardHint', 'Weiterleitungsziel bei Ihrem Anbieter: {{n}}', { n: primary })}
          </p>
        )}
        <p className="mt-2 flex items-start gap-2 text-xs text-slate-500">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
          {t(
            'settings:voice.forwardWarning',
            'Ob die Nummer des Anrufers erhalten bleibt, entscheidet Ihr bisheriger Anbieter — bitte mit einem Testanruf prüfen.',
          )}
        </p>
      </SettingsPanel>
    </div>
  );
}

/** Bestehende Nummer einer anderen (eigenen) Organisation zuweisen. */
function AssignNumberRow({ number, targets, onDone }: {
  number: string;
  targets: { id: string; name: string }[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [target, setTarget] = useState('');

  const assignMutation = useMutation({
    mutationFn: () => voiceService.assignNumber(number, target),
    onSuccess: () => {
      setTarget('');
      onDone();
      toast.success(t('settings:voice.assign.done', 'Nummer wurde der Organisation zugewiesen'));
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5">
      <p className="text-sm font-medium text-slate-700">
        {t('settings:voice.assign.title', 'Nummer einer anderen Organisation zuweisen')}
      </p>
      <p className="mt-0.5 text-xs text-slate-500">
        {t('settings:voice.assign.hint', 'Eingehende Anrufe, KI und Berechtigungen folgen der Nummer in die neue Organisation.')}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger className="h-9 w-full sm:w-64">
            <SelectValue placeholder={t('settings:voice.assign.pick', 'Organisation wählen …')} />
          </SelectTrigger>
          <SelectContent>
            {targets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          disabled={!target || assignMutation.isPending}
          onClick={() => assignMutation.mutate()}
        >
          {assignMutation.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
          {t('settings:voice.assign.action', 'Zuweisen')}
        </Button>
      </div>
    </div>
  );
}

const DEFAULT_ASSISTANT: AssistantInput = {
  ai_mode: 'off',
  name: 'Anna',
  greeting_mode: 'ai_first',
  greeting: '',
  instructions: '',
  language: 'de',
  tools: { orders: true, tickets: true, transfer: true },
};

const AI_LANGUAGES = ['de', 'en', 'tr', 'nl', 'fr', 'es', 'it', 'pl', 'pt'];

/**
 * KI-Telefonassistent — Selfservice pro Organisation.
 *
 * Der Kunde bestimmt Name, Begrüßungsverhalten, Anweisungen, Sprache und
 * welche VynDesk-Daten die KI einsehen darf. Das LLM dahinter (OpenRouter)
 * konfiguriert der Server — es taucht hier bewusst nirgends auf.
 */
function AssistantPanel({ enabled, orgId }: { enabled: boolean; orgId?: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['voice-assistant', orgId || ''],
    queryFn: () => voiceService.getAssistant(orgId),
    enabled,
  });

  const [form, setForm] = useState<AssistantInput>(DEFAULT_ASSISTANT);

  useEffect(() => {
    if (data?.config) setForm({ ...DEFAULT_ASSISTANT, ...data.config, ai_mode: data.ai_mode });
    else if (data) setForm((f) => ({ ...f, ai_mode: data.ai_mode }));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => voiceService.saveAssistant(form, orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voice-assistant'] });
      toast.success(t('settings:voice.ai.saved', 'KI-Assistent gespeichert'));
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  // Modus wirkt SOFORT (kein "Speichern" nötig): wer die KI ausschalten will,
  // will das jetzt — nicht nach einem weiteren Klick, den man leicht vergisst.
  const modeMutation = useMutation({
    mutationFn: (mode: AiMode) => voiceService.setAiMode(mode, orgId),
    onSuccess: (_r, mode) => {
      queryClient.invalidateQueries({ queryKey: ['voice-assistant'] });
      toast.success(
        mode === 'off'
          ? t('settings:voice.ai.modeOffApplied', 'KI ist aus — Anrufe gehen wieder direkt an Ihr Team')
          : t('settings:voice.ai.modeApplied', 'Modus übernommen'),
      );
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  const changeMode = (mode: AiMode) => {
    setForm((f) => ({ ...f, ai_mode: mode }));
    // Nur wenn der Assistent existiert — vor der ersten Einrichtung gibt es
    // serverseitig nichts umzuschalten, da gilt der Modus beim Speichern.
    if (data?.configured) modeMutation.mutate(mode);
  };

  if (!enabled) {
    return (
      <SettingsPanel
        title={t('settings:voice.ai.title', 'KI-Telefonassistent')}
        description={t('settings:voice.ai.needsPhone', 'Zuerst Rufnummer einrichten und Telefonie aktivieren — dann kann die KI ans Telefon.')}
      >
        <p className="text-sm text-slate-500">
          {t('settings:voice.ai.needsPhoneBody', 'Der Assistent beantwortet eingehende Anrufe, kann Bestellungen und Tickets nachschlagen und an Ihr Team weiterleiten.')}
        </p>
      </SettingsPanel>
    );
  }

  if (isLoading) {
    return (
      <SettingsPanel title={t('settings:voice.ai.title', 'KI-Telefonassistent')}>
        <div className="flex justify-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      </SettingsPanel>
    );
  }

  const set = <K extends keyof AssistantInput>(key: K, value: AssistantInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <SettingsPanel
      title={t('settings:voice.ai.title', 'KI-Telefonassistent')}
      description={t('settings:voice.ai.description', 'Beantwortet Anrufe automatisch, schlägt Bestellungen und Tickets nach und leitet bei Bedarf an Ihr Team weiter.')}
      action={
        data?.configured ? (
          data.ai_mode === 'off' ? (
            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
              {t('settings:voice.ai.badgeOff', 'KI aus')}
            </Badge>
          ) : (
            <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-700">
              {data.ai_mode === 'always'
                ? t('settings:voice.ai.badgeAlways', 'KI aktiv — nimmt alle Anrufe an')
                : t('settings:voice.ai.badgeFallback', 'KI aktiv — wenn niemand online')}
            </Badge>
          )
        ) : undefined
      }
    >
      <div className="space-y-4">
        {/* Modus */}
        <div className="space-y-1.5">
          <Label>{t('settings:voice.ai.mode', 'Wann geht die KI ans Telefon?')}</Label>
          <Select value={form.ai_mode} onValueChange={(v) => changeMode(v as AiMode)}>
            <SelectTrigger className="max-w-md"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="off">{t('settings:voice.ai.modeOff', 'Nie — KI ist aus')}</SelectItem>
              <SelectItem value="fallback">{t('settings:voice.ai.modeFallback', 'Nur wenn kein Mitarbeiter online ist')}</SelectItem>
              <SelectItem value="always">{t('settings:voice.ai.modeAlways', 'Immer — KI nimmt jeden Anruf zuerst an')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <SettingsFieldGrid>
          <div className="space-y-1.5">
            <Label htmlFor="ai-name">{t('settings:voice.ai.name', 'Name des Assistenten')}</Label>
            <Input id="ai-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Anna" />
          </div>
          <div className="space-y-1.5">
            <Label>{t('settings:voice.ai.language', 'Hauptsprache')}</Label>
            <Select value={form.language} onValueChange={(v) => set('language', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {AI_LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </SettingsFieldGrid>

        {/* Begrüßungsverhalten */}
        <div className="space-y-1.5">
          <Label>{t('settings:voice.ai.greetingMode', 'Gesprächsbeginn')}</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => set('greeting_mode', 'ai_first')}
              className={`flex-1 rounded-xl border px-3 py-2.5 text-left text-sm transition ${form.greeting_mode === 'ai_first' ? 'border-violet-300 bg-violet-50 text-violet-900' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              {t('settings:voice.ai.aiFirst', 'KI begrüßt zuerst')}
            </button>
            <button
              type="button"
              onClick={() => set('greeting_mode', 'caller_first')}
              className={`flex-1 rounded-xl border px-3 py-2.5 text-left text-sm transition ${form.greeting_mode === 'caller_first' ? 'border-violet-300 bg-violet-50 text-violet-900' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              {t('settings:voice.ai.callerFirst', 'KI wartet, bis der Anrufer spricht')}
            </button>
          </div>
          {form.greeting_mode === 'ai_first' && (
            <Input
              value={form.greeting}
              onChange={(e) => set('greeting', e.target.value)}
              placeholder={t('settings:voice.ai.greetingPlaceholder', 'Hallo, hier ist Anna von …  Wie kann ich helfen?')}
            />
          )}
        </div>

        {/* Anweisungen */}
        <div className="space-y-1.5">
          <Label htmlFor="ai-instructions">{t('settings:voice.ai.instructions', 'Anweisungen (optional)')}</Label>
          <Textarea
            id="ai-instructions"
            value={form.instructions}
            onChange={(e) => set('instructions', e.target.value)}
            rows={4}
            placeholder={t('settings:voice.ai.instructionsPlaceholder', 'z. B. Öffnungszeiten, Rückgaberegeln, Tonfall, was die KI NICHT zusagen darf …')}
          />
          <p className="text-xs text-slate-500">
            {t('settings:voice.ai.instructionsHint', 'Grundregeln (kurz antworten, nichts erfinden, Tools nutzen) setzt VynDesk automatisch.')}
          </p>
        </div>

        {/* VynDesk-Zugriffe */}
        <div className="space-y-1.5">
          <Label>{t('settings:voice.ai.tools', 'Was darf die KI?')}</Label>
          <div className="space-y-2 rounded-xl border border-slate-200 bg-white/60 p-3">
            {([
              ['orders', t('settings:voice.ai.toolOrders', 'Bestellungen nachschlagen (Status, Tracking)')],
              ['tickets', t('settings:voice.ai.toolTickets', 'Bestehende Tickets nachschlagen')],
              ['transfer', t('settings:voice.ai.toolTransfer', 'An Mitarbeiter weiterleiten (wenn online)')],
            ] as const).map(([key, label]) => (
              <label key={key} className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-700">
                <Checkbox
                  checked={form.tools[key]}
                  onCheckedChange={(v) => set('tools', { ...form.tools, [key]: v === true })}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common:action.save', 'Speichern')}
          </Button>
        </div>
      </div>
    </SettingsPanel>
  );
}

/**
 * Regulatorische Nachweise für Nummernbestellungen (z. B. deutsche Nummern).
 *
 * Die Anforderungen kommen dynamisch von Telnyx — Name/Beschreibung/Beispiel
 * werden bewusst UNVERÄNDERT angezeigt (regulatorischer Text; eine Übersetzung
 * riskiert falsche Angaben). Drei Feldtypen: Text, Dokument-Upload, Adresse.
 */
function RequirementsPanel({ orgId }: { orgId?: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: orders, isLoading } = useQuery({
    queryKey: ['voice-requirements', orgId || ''],
    queryFn: () => voiceService.listRequirements(orgId),
    refetchInterval: 60_000,
    retry: false,
  });

  /** requirement_id → eingegebener/erzeugter field_value */
  const [values, setValues] = useState<Record<string, string>>({});
  /** requirement_id → Upload/Anlegen läuft */
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  /** requirement_id (address) → Formularfelder */
  const [addr, setAddr] = useState<Record<string, { business_name: string; street_address: string; locality: string; postal_code: string }>>({});

  const submitMutation = useMutation({
    mutationFn: (order: PendingRequirementOrder) =>
      voiceService.submitRequirements(
        order.sub_number_order_id,
        order.requirements
          .filter((r) => values[r.requirement_id])
          .map((r) => ({ requirement_id: r.requirement_id, field_value: values[r.requirement_id] })),
        orgId,
      ),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['voice-requirements'] });
      queryClient.invalidateQueries({ queryKey: ['voice-numbers'] });
      toast.success(
        r.requirements_met
          ? t('settings:voice.req.done', 'Alle Nachweise übermittelt — die Nummer wird jetzt geprüft und aktiviert.')
          : t('settings:voice.req.partial', 'Angaben übermittelt — es fehlen noch weitere Nachweise.'),
      );
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  const setBusyFor = (id: string, v: boolean) => setBusy((b) => ({ ...b, [id]: v }));

  const uploadDoc = async (req: NumberRequirement, file: File) => {
    setBusyFor(req.requirement_id, true);
    try {
      const r = await voiceService.uploadRequirementDocument(file, orgId);
      setValues((v) => ({ ...v, [req.requirement_id]: r.documentId }));
      toast.success(t('settings:voice.req.uploaded', '{{name}} hochgeladen', { name: file.name }));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('settings:voice.req.uploadFailed', 'Upload fehlgeschlagen'));
    } finally {
      setBusyFor(req.requirement_id, false);
    }
  };

  const saveAddress = async (req: NumberRequirement, country: string) => {
    const a = addr[req.requirement_id];
    if (!a?.street_address?.trim() || !a?.locality?.trim()) {
      toast.error(t('settings:voice.req.addressMissing', 'Straße und Ort sind Pflicht'));
      return;
    }
    setBusyFor(req.requirement_id, true);
    try {
      const r = await voiceService.createRequirementAddress({ ...a, country_code: country }, orgId);
      setValues((v) => ({ ...v, [req.requirement_id]: r.addressId }));
      toast.success(t('settings:voice.req.addressSaved', 'Adresse gespeichert'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen'));
    } finally {
      setBusyFor(req.requirement_id, false);
    }
  };

  if (isLoading || !orders?.length) return null; // gereklilik yoksa panel hiç görünmez

  return (
    <>
      {orders.map((order) => {
        const openReqs = order.requirements.filter((r) => !r.field_value);
        const filled = openReqs.filter((r) => values[r.requirement_id]).length;
        return (
          <SettingsPanel
            key={order.sub_number_order_id}
            title={t('settings:voice.req.title', 'Nachweise erforderlich — {{number}}', { number: order.phone_numbers.join(', ') })}
            description={t(
              'settings:voice.req.description',
              'Für Rufnummern in {{country}} verlangt der Netzbetreiber die folgenden Nachweise. Die Nummer bleibt bis zur Prüfung inaktiv.',
              { country: order.country_code },
            )}
            action={
              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                {filled}/{openReqs.length} {t('settings:voice.req.provided', 'bereit')}
              </Badge>
            }
          >
            <div className="space-y-4">
              {openReqs.map((req) => {
                const done = Boolean(values[req.requirement_id]);
                return (
                  <div key={req.requirement_id} className="rounded-xl border border-slate-200 p-3.5 dark:border-white/10">
                    <div className="mb-1 flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{req.name}</p>
                      {done && <Check className="h-4 w-4 shrink-0 text-emerald-600" />}
                    </div>
                    {req.description && <p className="mb-2 text-xs leading-5 text-slate-500">{req.description}</p>}
                    {req.example && (
                      <p className="mb-2 text-[11px] italic text-slate-400">
                        {t('settings:voice.req.example', 'Beispiel')}: {req.example}
                      </p>
                    )}

                    {req.field_type === 'textual' && (
                      <Input
                        value={values[req.requirement_id] || ''}
                        onChange={(e) => setValues((v) => ({ ...v, [req.requirement_id]: e.target.value }))}
                        placeholder={t('settings:voice.req.textPlaceholder', 'Angabe eintragen …')}
                      />
                    )}

                    {req.field_type === 'document' && (
                      <label className={cn(
                        'flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-sm transition',
                        done
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10'
                          : 'border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700 dark:border-white/15',
                      )}>
                        {busy[req.requirement_id]
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : done ? <Check className="h-4 w-4" /> : <FileUp className="h-4 w-4" />}
                        {done
                          ? t('settings:voice.req.docDone', 'Dokument hochgeladen — anderes wählen?')
                          : t('settings:voice.req.docPick', 'Dokument auswählen (PDF, JPG, PNG)')}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadDoc(req, f); e.target.value = ''; }}
                        />
                      </label>
                    )}

                    {req.field_type === 'address' && (
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Input
                          placeholder={t('settings:voice.req.company', 'Firma (optional)')}
                          value={addr[req.requirement_id]?.business_name || ''}
                          onChange={(e) => setAddr((a) => ({ ...a, [req.requirement_id]: { ...(a[req.requirement_id] || { street_address: '', locality: '', postal_code: '', business_name: '' }), business_name: e.target.value } }))}
                        />
                        <Input
                          placeholder={t('settings:voice.req.street', 'Straße + Hausnummer *')}
                          value={addr[req.requirement_id]?.street_address || ''}
                          onChange={(e) => setAddr((a) => ({ ...a, [req.requirement_id]: { ...(a[req.requirement_id] || { street_address: '', locality: '', postal_code: '', business_name: '' }), street_address: e.target.value } }))}
                        />
                        <Input
                          placeholder={t('settings:voice.req.zip', 'PLZ')}
                          value={addr[req.requirement_id]?.postal_code || ''}
                          onChange={(e) => setAddr((a) => ({ ...a, [req.requirement_id]: { ...(a[req.requirement_id] || { street_address: '', locality: '', postal_code: '', business_name: '' }), postal_code: e.target.value } }))}
                        />
                        <Input
                          placeholder={t('settings:voice.req.city', 'Ort *')}
                          value={addr[req.requirement_id]?.locality || ''}
                          onChange={(e) => setAddr((a) => ({ ...a, [req.requirement_id]: { ...(a[req.requirement_id] || { street_address: '', locality: '', postal_code: '', business_name: '' }), locality: e.target.value } }))}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="sm:col-span-2"
                          disabled={busy[req.requirement_id] || done}
                          onClick={() => void saveAddress(req, order.country_code)}
                        >
                          {busy[req.requirement_id] && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                          {done ? t('settings:voice.req.addressDone', 'Adresse hinterlegt ✓') : t('settings:voice.req.addressSave', 'Adresse übernehmen')}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="flex justify-end">
                <Button
                  onClick={() => submitMutation.mutate(order)}
                  disabled={submitMutation.isPending || filled === 0}
                >
                  {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Upload className="mr-2 h-4 w-4" />
                  {t('settings:voice.req.submit', 'Nachweise übermitteln')} ({filled}/{openReqs.length})
                </Button>
              </div>
            </div>
          </SettingsPanel>
        );
      })}
    </>
  );
}
